import { useEffect, useRef, useState } from 'react'
import { API_MUTATION_EVENT } from '../../lib/apiEvents'
import { consoleErrorMessage, fetchStoryRequests, type Story, type StoryRequests } from './api'

export default function RequestsPanel({
  story,
  onBack,
  onClose,
}: {
  story: Story
  onBack: () => void
  onClose: () => void
}) {
  const [read, setRead] = useState<{ value?: StoryRequests; error?: unknown }>({})
  const [retry, setRetry] = useState(0)
  const back = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    back.current?.focus()
  }, [])
  useEffect(() => {
    const controller = new AbortController()
    let pending = false,
      again = false
    const pull = (background: boolean) => {
      if (controller.signal.aborted) return
      if (pending) {
        again = true
        return
      }
      pending = true
      fetchStoryRequests(story.id, controller.signal, background)
        .then((value) => {
          if (!controller.signal.aborted) setRead({ value })
        })
        .catch((error: unknown) => {
          if (!controller.signal.aborted) setRead((previous) => ({ ...previous, error }))
        })
        .finally(() => {
          pending = false
          if (again && !controller.signal.aborted) {
            again = false
            pull(true)
          }
        })
    }
    pull(false)
    const timer = setInterval(() => pull(true), 30000)
    const visible = () => {
      if (document.visibilityState === 'visible') pull(true)
    }
    const changed = () => pull(true)
    window.addEventListener('focus', visible)
    document.addEventListener('visibilitychange', visible)
    window.addEventListener(API_MUTATION_EVENT, changed)
    return () => {
      controller.abort()
      clearInterval(timer)
      window.removeEventListener('focus', visible)
      document.removeEventListener('visibilitychange', visible)
      window.removeEventListener(API_MUTATION_EVENT, changed)
    }
  }, [story.id, retry])
  const value = read.value
  return (
    <>
      <div className="console-scrim" onClick={onClose} />
      <aside
        className="console-panel"
        role="dialog"
        aria-label="Request letters"
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault()
            onBack()
          }
        }}
      >
        <button ref={back} type="button" className="vbtn cp-requests-back" onClick={onBack}>
          ‹ Stories
        </button>
        <div className="cp-head">
          <span className="cp-title">Request letters</span>
          <button type="button" className="cp-close" aria-label="Close" onClick={onClose}>
            ✕
          </button>
        </div>
        <p className="cp-sub">
          {value?.fund_name ?? story.name}
          <br />
          Letters recorded by the depositary.
        </p>
        {read.error ? (
          <div role="alert">
            <p className="cp-sub">
              {consoleErrorMessage(read.error)}
              {value?.letters.length ? ' Previously loaded letters are shown below.' : ''}
            </p>
            <button className="vbtn" type="button" onClick={() => setRetry((n) => n + 1)}>
              Retry
            </button>
          </div>
        ) : null}
        {!value && !read.error ? (
          <p className="cp-sub" role="status">
            Loading request letters…
          </p>
        ) : null}
        {value?.message ? (
          <p className="cp-sub" role="status">
            {value.message}
          </p>
        ) : null}
        {!read.error && value?.state === 'available' && !value.letters.length ? (
          <p className="cp-sub">No request letters recorded yet.</p>
        ) : null}
        {value?.letters.map((letter) => (
          <article className="cp-story cp-letter" key={letter.id}>
            <h3 className="cp-story-name">{letter.subject || 'Request letter'}</h3>
            <p className="cp-story-meta">
              To: {letter.recipient || 'Recipient not recorded'}
              <br />
              Recorded{' '}
              {new Intl.DateTimeFormat('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                timeZone: 'UTC',
              }).format(new Date(letter.recorded_at))}
            </p>
            <div className="cp-letter-body">{letter.body}</div>
          </article>
        ))}
        {value?.more_available ? (
          <p className="cp-sub">Showing recent letters. More letters are recorded.</p>
        ) : null}
      </aside>
    </>
  )
}
